import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FormDialog } from '@/components/common'
import { DatePicker } from '@/components/ui/date-picker'
import { Textarea } from '@/components/ui/textarea'
import {
  useCostCenters, useDepartments, useDesignations, useDivisions, useGrades, useLocations,
} from '@/features/org'
import { apiClient, toApiProblem } from '@/lib/api/client'
import { todayLocal } from '@/lib/dates'
import { useEmployeeOptions } from '../api'
import type { AssignmentView } from '../types'
import { ChoiceField, Field, OptionField } from './EmployeeFields'

/** Why a placement row exists. The API's own list, in the words somebody would use. */
const REASONS = [
  { value: 'TRANSFER', label: 'Transfer' },
  { value: 'PROMOTION', label: 'Promotion' },
  { value: 'COST_CENTRE_CHANGE', label: 'Cost centre change' },
  { value: 'REVERT', label: 'Returned from secondment' },
  { value: 'CORRECTION', label: 'Correction to what was recorded' },
  { value: 'REHIRE', label: 'Rehire' },
] as const

/**
 * Recording a dated move.
 *
 * <p>This is what "edit the placement" has to mean. A department, grade or manager is only
 * true as of a date: overwriting the row would rewrite where somebody sat last month as well
 * as this month, and every attendance and payroll figure that resolved the old placement would
 * stop reconciling against the report that explains it.
 *
 * <p>Distinct from a transfer request, which is the same change put through an approval. This
 * one writes the assignment directly, and exists for the cases a workflow cannot serve: a
 * correction to something recorded wrongly, and a backdated move agreed off-system. Fields
 * left alone carry forward from the placement in force, so a cost-centre change does not
 * quietly restate the department.
 */
export function RecordPlacementDialog({
  employeeId, current, open, onOpenChange, initialReason = 'TRANSFER',
}: {
  employeeId: string
  current: AssignmentView | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
  /** What the change is presumed to be. The page remounts the dialog per opening to apply it. */
  initialReason?: (typeof REASONS)[number]['value']
}) {
  const queryClient = useQueryClient()
  const departments = useDepartments()
  const divisions = useDivisions()
  const designations = useDesignations()
  const grades = useGrades()
  const locations = useLocations()
  const costCenters = useCostCenters()
  const managers = useEmployeeOptions()

  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState({
    effectiveFrom: todayLocal(),
    reason: initialReason,
    departmentId: '', divisionId: '', designationId: '', gradeId: '', locationId: '',
    costCenterId: '', managerEmployeeId: '', notes: '',
  })

  const create = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await apiClient.post<AssignmentView>(
        `/v1/employees/${employeeId}/assignments`, body)).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['employees'] })
    },
  })

  function set<K extends keyof typeof draft>(key: K, value: string) {
    setDraft((previous) => ({ ...previous, [key]: value }))
  }

  async function submit() {
    setError(null)
    try {
      await create.mutateAsync({
        effectiveFrom: draft.effectiveFrom,
        reason: draft.reason,
        // Blank means "unchanged", and the server carries the current value forward. Sending
        // an empty string would be read as "clear it".
        departmentId: draft.departmentId || current?.departmentId,
        divisionId: draft.divisionId || current?.divisionId,
        designationId: draft.designationId || current?.designationId,
        gradeId: draft.gradeId || current?.gradeId,
        locationId: draft.locationId || current?.locationId,
        costCenterId: draft.costCenterId || current?.costCenterId,
        managerEmployeeId: draft.managerEmployeeId || current?.managerEmployeeId,
        notes: draft.notes || null,
      })
      onOpenChange(false)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'The placement could not be recorded.')
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Change job or placement"
      description="A move is a new dated row, never an edit of the old one — so last month's figures keep resolving to where this person actually sat."
      size="lg"
      submitLabel="Record the move"
      loading={create.isPending}
      error={error}
      onSubmit={() => { void submit() }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="placement-from" label="In force from" required
          hint="Anything before this date keeps the placement it had."
        >
          <DatePicker id="placement-from" value={draft.effectiveFrom} required
            onChange={(event) => set('effectiveFrom', event.target.value)} />
        </Field>
        <ChoiceField id="placement-reason" label="Reason" required choices={REASONS}
          emptyLabel="Choose a reason"
          value={draft.reason} onChange={(value) => set('reason', value)} />
        <OptionField id="placement-department" label="Department"
          emptyLabel={current?.departmentName ? `Unchanged — ${current.departmentName}` : 'Not set'}
          options={departments.data}
          value={draft.departmentId} onChange={(value) => set('departmentId', value)} />
        <OptionField id="placement-division" label="Division"
          emptyLabel={current?.divisionName ? `Unchanged — ${current.divisionName}` : 'Not set'}
          options={divisions.data}
          value={draft.divisionId} onChange={(value) => set('divisionId', value)} />
        <OptionField id="placement-designation" label="Designation"
          emptyLabel={current?.designationName ? `Unchanged — ${current.designationName}` : 'Not set'}
          options={designations.data}
          value={draft.designationId} onChange={(value) => set('designationId', value)} />
        <OptionField id="placement-grade" label="Grade"
          emptyLabel={current?.gradeName ? `Unchanged — ${current.gradeName}` : 'Not set'}
          options={grades.data}
          value={draft.gradeId} onChange={(value) => set('gradeId', value)} />
        <OptionField id="placement-location" label="Location"
          emptyLabel={current?.locationName ? `Unchanged — ${current.locationName}` : 'Not set'}
          options={locations.data}
          value={draft.locationId} onChange={(value) => set('locationId', value)} />
        <OptionField id="placement-cost-center" label="Cost centre"
          emptyLabel={current?.costCenterName ? `Unchanged — ${current.costCenterName}` : 'Not set'}
          options={costCenters.data}
          value={draft.costCenterId} onChange={(value) => set('costCenterId', value)} />
        <OptionField id="placement-manager" label="Reports to"
          emptyLabel={current?.managerName ? `Unchanged — ${current.managerName}` : 'Nobody'}
          options={managers.data?.map((option) => ({
            id: option.id,
            name: `${option.displayName} (${option.employeeCode})`,
          }))}
          value={draft.managerEmployeeId}
          onChange={(value) => set('managerEmployeeId', value)} />
        <div className="sm:col-span-2">
          <Field id="placement-notes" label="Notes"
            hint="What this move was, in a sentence. It shows on the timeline."
          >
            <Textarea id="placement-notes" rows={2} value={draft.notes}
              onChange={(event) => set('notes', event.target.value)} />
          </Field>
        </div>
      </div>
    </FormDialog>
  )
}
